import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { cn } from "@/cn";
import { getProvider, updateProvider, type ProviderConfig } from "@/api/providers";
import {
  TYPE_LABELS,
  TYPE_COLORS,
  featureLabel,
  typeHelpText,
  hasIntegrationDetails,
  hasConfigData,
  formatProviderDate,
} from "@/lib/provider-ui";

function CopyIdButton({ id }: { id: string }) {
  const [copied, setCopied] = useState(false);

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(id);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // ignore
    }
  }

  return (
    <button
      type="button"
      onClick={handleCopy}
      className="rounded-full bg-muted px-2 py-0.5 text-[10px] text-muted-foreground transition hover:text-foreground"
      title="Copy provider ID"
    >
      {copied ? "Copied!" : `ID: ${id}`}
    </button>
  );
}

function ConfigBlock({ label, data }: { label: string; data: Record<string, unknown> }) {
  return (
    <div className="rounded-lg border border-border bg-card p-5">
      <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {label}
      </h3>
      <pre className="overflow-x-auto rounded bg-muted px-3 py-2 text-xs text-foreground">
        {JSON.stringify(data, null, 2)}
      </pre>
    </div>
  );
}

function ProviderActions({ provider }: { provider: ProviderConfig }) {
  const primaryAction =
    provider.type === "board" || provider.type === "signal"
      ? { to: "/lists?tab=providers", label: "Choose as signal source" }
      : provider.type === "ats"
        ? { to: "/lists?tab=sources", label: "Configure tracked companies" }
        : { to: "/settings/integrations", label: "Integrations" };

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-border bg-card p-5">
        <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Actions
        </h3>
        <div className="flex flex-col gap-2">
          <Link
            to={primaryAction.to}
            className="rounded-md bg-brand px-3 py-2 text-center text-sm font-medium text-brand-foreground transition hover:opacity-90"
          >
            {primaryAction.label}
          </Link>
          {provider.website && (
            <a
              href={provider.website}
              target="_blank"
              rel="noopener noreferrer"
              className="rounded-md border border-border px-3 py-2 text-center text-sm text-foreground transition hover:bg-muted/20"
            >
              Visit website
            </a>
          )}
          {provider.docsUrl && (
            <a
              href={provider.docsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="rounded-md border border-border px-3 py-2 text-center text-sm text-foreground transition hover:bg-muted/20"
            >
              View documentation
            </a>
          )}
        </div>
      </div>

      {!provider.enabled && (
        <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-xs text-amber-600 dark:text-amber-400">
          This provider is globally disabled. Enable it here before adding it to your scan or
          tracked companies.
        </div>
      )}

      <div className="rounded-lg border border-border bg-card p-5">
        <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          How it works
        </h3>
        <p className="text-xs text-muted-foreground">{typeHelpText(provider.type)}</p>
      </div>
    </div>
  );
}

export function SettingsProviderDetailPage() {
  const { id } = useParams<{ id: string }>();
  const qc = useQueryClient();

  const { data: provider, isLoading, error } = useQuery<ProviderConfig>({
    queryKey: ["provider", id],
    queryFn: () => getProvider(id!),
    enabled: !!id,
    staleTime: 30_000,
  });

  const toggleMutation = useMutation({
    mutationFn: (enabled: boolean) => updateProvider(id!, { enabled }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["provider", id] });
      qc.invalidateQueries({ queryKey: ["providers"] });
    },
  });

  const createdLabel = formatProviderDate(provider?.createdAt);
  const updatedLabel = formatProviderDate(provider?.updatedAt);

  return (
    <div className="space-y-6">
      <header className="mb-8">
        <Link
          to="/settings/signals"
          className="text-xs text-muted-foreground hover:text-foreground"
        >
          &larr; Back to providers
        </Link>
      </header>

      {isLoading && (
        <div className="flex items-center justify-center py-16">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-muted border-t-brand" />
        </div>
      )}
      {error && <p className="text-sm text-destructive">Failed to load provider.</p>}

      {provider && (
        <div className="lg:grid lg:grid-cols-3 lg:gap-6">
          <div className="mb-6 space-y-4 lg:col-span-1 lg:mb-0">
            <ProviderActions provider={provider} />
          </div>

          <div className="space-y-4 lg:col-span-2">
            <div className="rounded-lg border border-border bg-card p-5">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0 flex-1">
                  <h2 className="text-lg font-bold text-foreground">{provider.name}</h2>
                  <p className="mt-1 text-sm text-muted-foreground">
                    {provider.description || "No description available."}
                  </p>
                </div>
                <label className="relative inline-flex shrink-0 cursor-pointer items-center">
                  <input
                    type="checkbox"
                    checked={provider.enabled}
                    onChange={() => toggleMutation.mutate(!provider.enabled)}
                    disabled={toggleMutation.isPending}
                    className="peer sr-only"
                  />
                  <div className="h-5 w-9 rounded-full border border-border bg-muted after:absolute after:left-[2px] after:top-[2px] after:h-4 after:w-4 after:rounded-full after:bg-foreground after:transition-all peer-checked:bg-brand peer-checked:after:translate-x-full peer-checked:after:bg-brand-foreground" />
                </label>
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                <CopyIdButton id={provider.providerId} />
                <span
                  className={cn(
                    "rounded-full px-2 py-0.5 text-[10px] font-medium",
                    TYPE_COLORS[provider.type],
                  )}
                >
                  {TYPE_LABELS[provider.type]}
                </span>
                <span
                  className={cn(
                    "rounded-full px-2 py-0.5 text-[10px]",
                    provider.enabled
                      ? "bg-success/10 text-success"
                      : "bg-muted text-muted-foreground",
                  )}
                >
                  {provider.enabled ? "Enabled" : "Disabled"}
                </span>
              </div>
            </div>

            {provider.features && provider.features.length > 0 && (
              <div className="rounded-lg border border-border bg-card p-5">
                <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Capabilities
                </h3>
                <div className="flex flex-wrap gap-1.5">
                  {provider.features.map((f) => (
                    <span
                      key={f}
                      className="rounded-full bg-muted px-2 py-0.5 text-[10px] text-muted-foreground"
                    >
                      {featureLabel(f)}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {hasIntegrationDetails(provider) ? (
              <div className="rounded-lg border border-border bg-card p-5">
                <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Integration
                </h3>
                <div className="space-y-4">
                  {provider.urlPattern && (
                    <div>
                      <p className="mb-1 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                        URL pattern
                      </p>
                      <code className="block break-all rounded bg-muted px-3 py-2 text-xs text-foreground">
                        {provider.urlPattern}
                      </code>
                    </div>
                  )}
                  {provider.allowedHosts && provider.allowedHosts.length > 0 && (
                    <div>
                      <p className="mb-1 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                        Trusted hosts
                      </p>
                      <ul className="list-inside list-disc space-y-0.5">
                        {provider.allowedHosts.map((host) => (
                          <li key={host} className="text-xs text-muted-foreground">
                            {host}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  <div className="grid gap-3 sm:grid-cols-2">
                    {provider.supportsDetect !== undefined && (
                      <div>
                        <p className="mb-1 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                          Auto-detect
                        </p>
                        <p
                          className={cn(
                            "text-sm",
                            provider.supportsDetect ? "text-success" : "text-muted-foreground",
                          )}
                        >
                          {provider.supportsDetect ? "Supported" : "Not supported"}
                        </p>
                      </div>
                    )}
                    {provider.supportsApiUrl !== undefined && (
                      <div>
                        <p className="mb-1 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                          API URL override
                        </p>
                        <p
                          className={cn(
                            "text-sm",
                            provider.supportsApiUrl ? "text-success" : "text-muted-foreground",
                          )}
                        >
                          {provider.supportsApiUrl ? "Supported" : "Not supported"}
                        </p>
                      </div>
                    )}
                    {provider.timeoutMs !== undefined && (
                      <div>
                        <p className="mb-1 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                          Timeout
                        </p>
                        <p className="text-sm text-foreground">
                          {(provider.timeoutMs / 1000).toFixed(0)}s
                        </p>
                      </div>
                    )}
                    {provider.retryPolicy && (
                      <div>
                        <p className="mb-1 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                          Retry policy
                        </p>
                        <p className="text-sm text-foreground">{provider.retryPolicy}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="rounded-lg border border-border bg-card p-5">
                <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Integration
                </h3>
                <p className="text-sm text-muted-foreground">
                  No integration metadata is available for this provider yet. {typeHelpText(provider.type)}
                </p>
              </div>
            )}

            {hasConfigData(provider.defaultConfig) && (
              <ConfigBlock label="Default configuration" data={provider.defaultConfig} />
            )}
            {hasConfigData(provider.userOverrides) && (
              <ConfigBlock label="User overrides" data={provider.userOverrides} />
            )}

            {(createdLabel || updatedLabel) && (
              <div className="rounded-lg border border-border bg-card p-5">
                <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Metadata
                </h3>
                <dl className="grid gap-2 text-sm sm:grid-cols-2">
                  {createdLabel && (
                    <div>
                      <dt className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                        Created
                      </dt>
                      <dd className="text-foreground">{createdLabel}</dd>
                    </div>
                  )}
                  {updatedLabel && (
                    <div>
                      <dt className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                        Updated
                      </dt>
                      <dd className="text-foreground">{updatedLabel}</dd>
                    </div>
                  )}
                </dl>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
